import { Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";
import type { Payment } from "@/model/payment";

// Note: Font registration from external URLs often fails in browser environments
// due to CORS and format issues. Using Helvetica which is built into react-pdf.
// To use DM Sans, fonts would need to be hosted locally in the public folder.

interface ReceiptPDFProps {
  payment: Payment;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("de-DE", {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount) + " kr.";
};

const formatDate = (timestamp: string | number | { $numberLong?: string } | undefined): string => {
  if (!timestamp) return "";

  let date: Date;

  if (typeof timestamp === "string") {
    const num = parseInt(timestamp, 10);
    if (!isNaN(num)) {
      date = new Date(num * 1000);
    } else {
      date = new Date(timestamp);
    }
  } else if (typeof timestamp === "number") {
    date = new Date(timestamp * 1000);
  } else if (timestamp.$numberLong) {
    date = new Date(parseInt(timestamp.$numberLong, 10) * 1000);
  } else {
    return "";
  }

  if (isNaN(date.getTime())) return "";

  const dateStr = new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);

  const timeStr = new Intl.DateTimeFormat("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);

  return `${dateStr} kl. ${timeStr}`;
};

const formatCreditorExternalId = (id: string | undefined): string | undefined => {
  if (!id) return undefined;
  // Add dash before the last 4 digits (e.g., "1102982269" -> "110298-2269")
  if (id.length > 4) {
    return `${id.slice(0, -4)}-${id.slice(-4)}`;
  }
  return id;
};

const formatDebtorBban = (bban: string | undefined): string | undefined => {
  if (!bban) return undefined;
  // Format as "1234-26-123456" (4 digits, dash, 2 digits, dash, 6 digits)
  const cleaned = bban.replace(/\D/g, ""); // Remove non-digits
  if (cleaned.length === 12) {
    return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 6)}-${cleaned.slice(6)}`;
  }
  return bban; // Return as-is if not 12 digits
};

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
  },
  container: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  content: {
    flexGrow: 1,
  },
  header: {
    textAlign: "center",
    marginBottom: 24,
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    fontFamily: "Helvetica",
    color: "#1a1a1a",
    letterSpacing: -0.2,
    marginBottom: 8,
  },
  merchantSection: {
    marginBottom: 24,
    paddingBottom: 20,
    borderBottom: "2px solid #000",
  },
  merchantName: {
    fontSize: 16,
    fontWeight: 700,
    color: "#1a1a1a",
    fontFamily: "Helvetica",
    textAlign: "center",
    marginBottom: 4,
  },
  merchantExternalId: {
    fontSize: 10,
    color: "#666",
    fontFamily: "Helvetica",
    textAlign: "center",
  },
  itemsSection: {
    marginBottom: 20,
  },
  receiptItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 12,
    fontSize: 12,
    color: "#1a1a1a",
    fontFamily: "Helvetica",
    lineHeight: 1.5,
  },
  receiptItemName: {
    flex: 1,
    fontSize: 12,
    color: "#1a1a1a",
    fontFamily: "Helvetica",
  },
  receiptItemPrice: {
    fontSize: 12,
    color: "#1a1a1a",
    fontWeight: 600,
    fontFamily: "Helvetica",
    marginLeft: 12,
  },
  receiptItemDetails: {
    fontSize: 10,
    color: "#666",
    fontFamily: "Helvetica",
    marginTop: 2,
  },
  quantityRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
    fontSize: 12,
    color: "#1a1a1a",
    fontFamily: "Helvetica",
  },
  quantityLabel: {
    fontSize: 12,
    color: "#666",
    fontWeight: 500,
    fontFamily: "Helvetica",
  },
  quantityValue: {
    fontSize: 12,
    color: "#1a1a1a",
    fontWeight: 600,
    fontFamily: "Helvetica",
  },
  divider: {
    borderTop: "1px solid #e5e5e5",
    marginTop: 8,
    marginBottom: 16,
  },
  totalSection: {
    marginTop: 16,
    paddingTop: 16,
    borderTop: "2px solid #000",
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  totalLabel: {
    fontSize: 14,
    fontWeight: 700,
    color: "#1a1a1a",
    fontFamily: "Helvetica",
    textTransform: "uppercase",
  },
  totalAmount: {
    fontSize: 18,
    fontWeight: 700,
    color: "#1a1a1a",
    fontFamily: "Helvetica",
  },
  paymentInfo: {
    marginTop: 20,
    paddingTop: 16,
    borderTop: "1px solid #e5e5e5",
  },
  paymentInfoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 6,
    fontSize: 10,
    color: "#666",
    fontFamily: "Helvetica",
  },
  paymentInfoLabel: {
    fontSize: 10,
    color: "#666",
    fontFamily: "Helvetica",
  },
  paymentInfoValue: {
    fontSize: 10,
    color: "#1a1a1a",
    fontWeight: 500,
    fontFamily: "Helvetica",
  },
  footer: {
    textAlign: "center",
    paddingTop: 20,
    borderTop: "1px solid #e5e5e5",
    marginTop: "auto",
  },
  footerLogoContainer: {
    alignItems: "center",
    justifyContent: "center",
  },
  footerLogoImg: {
    height: 40,
    width: 70,
  },
});

export const ReceiptPDF = ({ payment }: ReceiptPDFProps) => {
  const amount = payment.amount || 0;
  const merchantName = payment.merchantName || payment.creditorName || "";
  // Check for creditorExternalId in multiple places (top-level, nested, and handle typo variant)
  const creditorExternalId = formatCreditorExternalId(payment.creditorExternalId);
  const items = payment.items || [];
  const paymentDate = formatDate(payment.createdAt);

  // Get absolute URL for the logo image
  const logoUrl = typeof window !== "undefined"
    ? `${window.location.origin}/images/knuid-blikk-lightbg.png`
    : "/images/knuid-blikk-lightbg.png";

  // Get items with prices if available
  const getReceiptItems = (): Array<{ name: string; price?: number; quantity?: number }> => {
    const receiptItems: Array<{ name: string; price?: number; quantity?: number }> = [];

    // For Blink payments, add blinkName first if available
    if (payment.type === "blink" && payment.blinkName) {
      const blinkInItems = items.some(item => item.name === payment.blinkName);
      if (!blinkInItems) {
        receiptItems.push({ name: payment.blinkName });
      }
    }

    // Add items with prices if available
    if (items.length > 0) {
      items.forEach((item) => {
        if (item.name) {
          const unitPrice = item.unitPrice ? parseFloat(item.unitPrice) : undefined;
          const itemQuantity = item.quantity || 1;
          const totalPrice = unitPrice !== undefined ? unitPrice * itemQuantity : undefined;
          receiptItems.push({
            name: item.name,
            price: totalPrice,
            quantity: itemQuantity > 1 ? itemQuantity : undefined,
          });
        }
      });
    }

    // Fallback to remittance information if no items
    if (receiptItems.length === 0 && payment.message) {
      receiptItems.push({ name: payment.message });
    }

    return receiptItems;
  };

  const receiptItems = getReceiptItems();

  // Calculate if we should show payment-level quantity (only if not already shown in items)
  const showPaymentQuantity = payment.quantity !== undefined &&
    payment.quantity !== null &&
    items.length === 0;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          <View style={styles.content}>
            {/* Header */}
            <View style={styles.header}>
              <Text style={styles.title}>Greiðslukvittun</Text>
            </View>

            {/* Merchant Name */}
            {merchantName && (
              <View style={styles.merchantSection}>
                <Text style={styles.merchantName}>{merchantName}</Text>
                {creditorExternalId && (
                  <Text style={styles.merchantExternalId}>{creditorExternalId}</Text>
                )}
              </View>
            )}

            {/* Items Section */}
            {receiptItems.length > 0 && (
              <View style={styles.itemsSection}>
                {receiptItems.map((item, index) => (
                  <View key={index} style={styles.receiptItem}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.receiptItemName}>
                        {item.name}
                      </Text>
                      {item.quantity && (
                        <Text style={styles.receiptItemDetails}>
                          Fjöldi: {item.quantity}
                        </Text>
                      )}
                    </View>
                    {item.price !== undefined && (
                      <Text style={styles.receiptItemPrice}>
                        {formatCurrency(item.price)}
                      </Text>
                    )}
                  </View>
                ))}
              </View>
            )}

            {/* Payment-level Quantity (only if not shown in items) */}
            {showPaymentQuantity && (
              <View style={styles.quantityRow}>
                <Text style={styles.quantityLabel}>Fjöldi</Text>
                <Text style={styles.quantityValue}>{payment.quantity}</Text>
              </View>
            )}

            {/* Total Section */}
            <View style={styles.totalSection}>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Samtals greitt</Text>
                <Text style={styles.totalAmount}>
                  {formatCurrency(amount)}
                </Text>
              </View>
            </View>

            {/* Payment Info */}
            <View style={styles.paymentInfo}>
              {formatDebtorBban(payment.debtorBban) && (
                <View style={styles.paymentInfoRow}>
                  <Text style={styles.paymentInfoLabel}>Greiðslureikningur</Text>
                  <Text style={styles.paymentInfoValue}>{formatDebtorBban(payment.debtorBban)}</Text>
                </View>
              )}
              {paymentDate && (
                <View style={styles.paymentInfoRow}>
                  <Text style={styles.paymentInfoLabel}>Dagsetning</Text>
                  <Text style={styles.paymentInfoValue}>{paymentDate}</Text>
                </View>
              )}
              {payment.id && (
                <View style={styles.paymentInfoRow}>
                  <Text style={styles.paymentInfoLabel}>Greiðslunúmer</Text>
                  <Text style={styles.paymentInfoValue}>{payment.id}</Text>
                </View>
              )}
            </View>
          </View>

          {/* Footer */}
          <View style={styles.footer}>
            <View style={styles.footerLogoContainer}>
              <Image
                src={logoUrl}
                style={styles.footerLogoImg}
              />
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};
